import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../../elements/Layout';
import Modal from '../../elements/Modal';
import AddContractForm from './AddContractForm';
import { deleteContract, editContractDate, fetchContracts, fetchCustomers, updateContractStatus } from '../../utils/api/api';
import { useNavigate } from 'react-router-dom';
import { CSVLink } from "react-csv";
import moment from 'moment';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';

// Icons
import {
  FaEye,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
  FaEllipsisH,
  FaCalendarAlt,
  FaFileContract,
  FaUser,
  FaFilter,
  FaFileExport,
  FaPlus
} from 'react-icons/fa';
import {
  MdDeleteForever,
  MdFilterList,
  MdSort,
  MdInfo,
  MdCheck,
  MdClose,
  MdAccessTime
} from 'react-icons/md';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { IoFilter, IoAdd, IoCalendarOutline, IoWarning } from 'react-icons/io5';

const headers = [
  { label: "ID", key: "id" },
  { label: "Title", key: "title" },
  { label: "Type", key: "type" },
  { label: "Customer ID", key: "customer_id" },
  { label: "Customer First Name", key: "customer.first_name" },
  { label: "Customer Last Name", key: "customer.last_name" },
  { label: "Project ID", key: "project_id" },
  { label: "Project Type", key: "project_type" },
  { label: "Start Date", key: "start_date" },
  { label: "End Date", key: "end_date" },
  { label: "Value", key: "value" },
  { label: "Terms", key: "terms" },
  { label: "Signatories", key: "signatories" },
  { label: "Is Draft", key: "is_draft" },
  { label: "Status", key: "status" },
];

const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const [addContract, setAddContract] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [customerIdFilter, setCustomerIdFilter] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [del, setDel] = useState(false);
  const [contractsPerPage, setContractsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [contractToDelete, setContractToDelete] = useState(null);

  const handleFormClose = () => setAddContract(false);

  const handleCustomerSelect = (selectedOption) => {
    setSelectedCustomer(selectedOption);
    setCurrentPage(1);
  };

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const response = await fetchCustomers({
          page: 0,
          pageSize: 0,
        });
        const formattedCustomers = response.customers.map(customer => ({
          value: customer.id,
          label: `${customer.first_name} ${customer.last_name}`,
        }));
        setCustomers(formattedCustomers);
      } catch (error) {
        toast.error('Failed to fetch customers.');
      }
    };

    loadCustomers();
  }, []);

  const getContracts = async (page, size) => {
    setLoading(true);
    try {
      const filters = {};
      if (selectedCustomer) filters.customer_id = selectedCustomer.value;
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.type = typeFilter;

      const data = await fetchContracts({
        page,
        pageSize: size,
        ...filters,
      });
      setContracts(data.contracts);
      setFilteredContracts(data.contracts);
      setTotalPages(data.meta.totalPages);
    } catch (err) {
      toast.error('Failed to fetch contracts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getContracts(currentPage, contractsPerPage);
  }, [currentPage, contractsPerPage, statusFilter, typeFilter, selectedCustomer]);

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'type':
        setTypeFilter(value);
        break;
      case 'customerId':
        setCustomerIdFilter(value);
        break;
      default:
        break;
    }
    setCurrentPage(1);
  };

  const openStatusModal = (contract) => {
    setSelectedContract(contract);
    setShowStatusModal(true);
  };

  const openEditModal = (contract) => {
    setSelectedContract(contract);
    setStartDate(new Date(contract.start_date));
    setEditModal(true);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedContract) return;
    setLoading(true);

    try {
      const payload = {
        id: selectedContract.id,
        status: newStatus,
      };
      await updateContractStatus(payload);
      setShowStatusModal(false);
      setSelectedContract(null);
      toast.success('Contract status updated successfully');
      getContracts(currentPage, contractsPerPage);
    } catch (err) {
      toast.error("Error updating contract status");
    } finally {
      setLoading(false);
    }
  };

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const openDeleteModal = (contract) => {
    setContractToDelete(contract);
    setDel(true);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteContract(contractToDelete.id);
      toast.success('Contract deleted successfully!');
      setDel(false);
      getContracts(currentPage, contractsPerPage);
    } catch (error) {
      toast.error('Failed to delete contract.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDate = async () => {
    setLoading(true);
    const updatedData = {
      id: selectedContract.id,
      start_date: startDate.toISOString().split('T')[0],
    };

    try {
      await editContractDate(updatedData);
      toast.success('Date updated successfully');
      getContracts(currentPage, contractsPerPage);
    } catch (error) {
      toast.error('Failed to update date');
      console.error("Error updating date:", error);
    } finally {
      setLoading(false);
      setEditModal(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 }
    }
  };

  // For skeleton loading
  const Skeleton = ({ className }) => (
    <motion.div
      initial={{ opacity: 0.5 }}
      animate={{ opacity: [0.5, 0.8, 0.5] }}
      transition={{ duration: 1.5, repeat: Infinity }}
      className={`bg-gray-200 rounded-lg ${className}`}
    />
  );

  return (
    <Layout backgroundColor="text-white" text={"Contracts"}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="space-y-6"
      >
        {/* Filters and Actions */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white/80 backdrop-blur-sm p-5 rounded-2xl shadow-sm"
        >
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="relative"
            >
              <select
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 rounded-xl outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200 w-full sm:w-auto min-w-[160px]"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <MdFilterList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="relative"
            >
              <select
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 rounded-xl outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200 w-full sm:w-auto min-w-[160px]"
              >
                <option value="">All Types</option>
                <option value="offer_letter">Offer Letter</option>
                <option value="sales_agreement">Sales Agreement</option>
                <option value="completion_letter">Completion Letter</option>
              </select>
              <HiOutlineDocumentText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="relative w-full sm:w-auto min-w-[240px]"
            >
              <Select
                options={customers}
                value={selectedCustomer}
                onChange={handleCustomerSelect}
                placeholder="Select a customer"
                className="text-gray-700 rounded-xl"
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: 'rgb(249 250 251)',
                    borderColor: 'rgb(229 231 235)',
                    borderRadius: '0.75rem',
                    minHeight: '48px',
                    padding: '0 0 0 2rem',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: 'rgb(209 213 219)'
                    }
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected
                      ? 'var(--color-secondary)'
                      : state.isFocused
                        ? 'rgba(var(--color-secondary-rgb), 0.1)'
                        : null,
                    color: state.isSelected ? 'white' : 'inherit'
                  })
                }}
              />
              <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm" />
            </motion.div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <CSVLink
                headers={headers}
                data={contracts}
                filename="contracts.csv"
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors shadow-sm"
              >
                <FaFileExport size={16} />
                <span>Export</span>
              </CSVLink>
            </motion.div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setAddContract(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-white rounded-xl hover:bg-secondary/90 transition-colors shadow-sm"
            >
              <FaPlus size={16} />
              <span>New Contract</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Contracts Table */}
        <motion.div
          variants={itemVariants}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
        >
          {loading ? (
            <div className="p-8 flex justify-center items-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full"
              />
            </div>
          ) : filteredContracts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              >
                <FaFileContract size={64} className="text-gray-300 mb-4" />
              </motion.div>
              <motion.h3
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-xl font-semibold text-gray-700 mb-2"
              >
                No Contracts Found
              </motion.h3>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-gray-500 mb-6 text-center max-w-md"
              >
                There are no contracts matching your current filter criteria. Try adjusting your filters or add a new contract.
              </motion.p>
              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setAddContract(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-secondary text-white rounded-xl shadow-sm hover:bg-secondary/90 transition-colors"
              >
                <FaPlus size={16} />
                <span>Add First Contract</span>
              </motion.button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contract</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredContracts.map((contract, index) => (
                    <motion.tr
                      key={contract.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      whileHover={{ backgroundColor: "#f9fafb" }}
                      className="group bg-white cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1 + (currentPage - 1) * contractsPerPage}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-secondary/10 rounded-full flex items-center justify-center">
                            <HiOutlineDocumentText className="h-5 w-5 text-secondary" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{contract.title}</div>
                            <div className="text-xs text-gray-500">{contract.type?.replace(/_/g, ' ') || 'Standard Contract'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 flex-shrink-0 bg-primary/10 text-primary rounded-full flex items-center justify-center uppercase">
                            {contract?.customer?.first_name?.[0] || '?'}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {`${(contract?.customer?.first_name || '').split(' ').slice(0, 2).join(' ')} ${(contract?.customer?.last_name || '').split(' ').slice(0, 2).join(' ')
                                }`.trim() || 'Unknown Client'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <IoCalendarOutline className="mr-2 text-gray-500" />
                          <span className="text-sm text-gray-700">
                            {moment(contract?.start_date).format("MMM D, YYYY")}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {contract.status === "pending" ? (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => openStatusModal(contract)}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                          >
                            <MdAccessTime className="mr-1" />
                            Pending Approval
                          </motion.button>
                        ) : (
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${contract.status === "approved"
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                              }`}
                          >
                            {contract.status === "approved" ? (
                              <><MdCheck className="mr-1" /> Approved</>
                            ) : (
                              <><MdClose className="mr-1" /> Cancelled</>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => navigate(`/dashboard/contracts/${contract.id}/view`)}
                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            <FaEye size={16} />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => openEditModal(contract)}
                            className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                          >
                            <FaEdit size={16} />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => openDeleteModal(contract)}
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            <MdDeleteForever size={18} />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {filteredContracts.length > 0 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative"
                >
                  <select
                    onChange={(e) => setContractsPerPage(Number(e.target.value))}
                    className="pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 rounded-xl outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200 w-full sm:w-auto"
                  >
                    <option value="10">10 per page</option>
                    <option value="20">20 per page</option>
                    <option value="40">40 per page</option>
                    <option value="80">80 per page</option>
                    <option value="140">140 per page</option>
                  </select>
                  <MdSort className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
                </motion.div>
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * contractsPerPage) + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * contractsPerPage, filteredContracts.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredContracts.length}</span> contracts
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-3 py-2 rounded-l-lg text-sm font-medium ${currentPage === 1
                        ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                        : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                    >
                      <FaChevronLeft className="h-4 w-4" />
                    </motion.button>

                    {/* Show first page, ellipsis, and nearby pages */}
                    {totalPages > 5 && currentPage > 3 && (
                      <>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => paginate(1)}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          1
                        </motion.button>
                        <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white">
                          <FaEllipsisH className="h-3 w-3" />
                        </span>
                      </>
                    )}

                    {/* Current and adjacent pages */}
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <motion.button
                            key={page}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => paginate(page)}
                            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${currentPage === page
                              ? 'z-10 bg-primary text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </motion.button>
                        );
                      }
                      return null;
                    })}

                    {/* Show ellipsis and last page */}
                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <>
                        <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white">
                          <FaEllipsisH className="h-3 w-3" />
                        </span>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => paginate(totalPages)}
                          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          {totalPages}
                        </motion.button>
                      </>
                    )}

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-3 py-2 rounded-r-lg text-sm font-medium ${currentPage === totalPages
                        ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                        : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                    >
                      <FaChevronRight className="h-4 w-4" />
                    </motion.button>
                  </nav>
                </div>
              </div>

              {/* Mobile pagination */}
              <div className="flex sm:hidden justify-between w-full">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === 1
                    ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                >
                  <FaChevronLeft className="mr-2 h-4 w-4" />
                  Previous
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${currentPage === totalPages
                    ? 'text-gray-300 bg-gray-50 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                >
                  Next
                  <FaChevronRight className="ml-2 h-4 w-4" />
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Add Contract Modal */}
      <AnimatePresence>
        {addContract && (
          <Modal title="New Contract" onClose={handleFormClose}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <AddContractForm
                onClose={() => {
                  handleFormClose();
                  getContracts(currentPage, contractsPerPage);
                }}
              />
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {del && (
          <Modal title="Delete Contract" onClose={() => setDel(false)}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-6"
            >
              <div className="flex items-start mb-4">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <IoWarning className="text-red-600 text-xl" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Confirm Contract Deletion
                  </h3>
                  <p className="text-gray-600">
                    Are you sure you want to permanently delete the contract titled "<span className="font-medium">{contractToDelete?.title}</span>" for "{contractToDelete?.customer?.first_name}"?
                  </p>
                  <p className="text-sm text-red-600 mt-2">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setDel(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white font-medium rounded-xl shadow-sm flex items-center"
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      Deleting...
                    </>
                  ) : (
                    <>Delete Contract</>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Edit Date Modal */}
      <AnimatePresence>
        {editModal && (
          <Modal title="Update Start Date" onClose={() => setEditModal(false)}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-6"
            >
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {selectedContract?.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  Update the start date for this contract.
                </p>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date:
                  </label>
                  <div className="relative">
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      dateFormat="yyyy/MM/dd"
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-secondary/30 transition-all duration-200"
                      showPopperArrow={false}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                    <FaCalendarAlt className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setEditModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSaveDate}
                  disabled={loading}
                  className="px-4 py-2 bg-secondary text-white font-medium rounded-xl shadow-sm flex items-center"
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      Saving...
                    </>
                  ) : (
                    <>Save Date</>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Status Change Modal */}
      <AnimatePresence>
        {showStatusModal && (
          <Modal title="Change Contract Status" onClose={() => setShowStatusModal(false)}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-6"
            >
              <div className="flex items-start mb-6">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <MdCheck className="text-green-600 text-xl" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Approve Contract
                  </h3>
                  <p className="text-gray-600">
                    Are you sure you want to approve the contract titled "<span className="font-medium">{selectedContract?.title}</span>"?
                  </p>
                  <p className="text-sm text-green-600 mt-2">
                    This will change the status from Pending to Approved.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowStatusModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleStatusChange('approved')}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white font-medium rounded-xl shadow-sm flex items-center"
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      Processing...
                    </>
                  ) : (
                    <>Approve Contract</>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default Contracts;